import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import { useLanguage } from '../contexts/LanguageContext';
import { GraduationCap, Users, Tent, School, BookOpen, ChevronRight, Clock, BarChart3 } from 'lucide-react';
import axios from 'axios';

const AUDIENCE_GROUPS = [
  { key: 'camp', labelEn: 'Summer Camps', labelFr: 'Camps d\'ete', icon: Tent, color: '#E8590C' },
  { key: 'afterschool', labelEn: 'After-School Centres', labelFr: 'Centres parascolaires', icon: Users, color: '#2F9E44' },
  { key: 'language_centre', labelEn: 'Language Centres', labelFr: 'Centres linguistiques', icon: BookOpen, color: '#7048E8' },
  { key: 'private_school', labelEn: 'Private Schools', labelFr: 'Ecoles privees', icon: School, color: '#1971C2' },
  { key: 'public_school', labelEn: 'Public Schools', labelFr: 'Ecoles publiques', icon: GraduationCap, color: '#C92A2A' },
];

export function ProgramCatalog() {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const isFr = lang === 'fr';

  useEffect(() => {
    axios.get(`${API}/programs`)
      .then(r => setPrograms(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const grouped = AUDIENCE_GROUPS.map(group => ({
    ...group,
    programs: programs.filter(p => p.audience_type === group.key),
  }));

  if (loading) {
    return (
      <div data-testid="programs-loading" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <div style={{ textAlign: 'center', color: '#64748B' }}>
          <GraduationCap size={48} style={{ marginBottom: 12, opacity: 0.4 }} />
          <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.1rem' }}>
            {isFr ? 'Chargement des programmes...' : 'Loading programs...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="program-catalog" style={{ height: '100%', overflow: 'auto', padding: '2rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{
          fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.6rem, 3vw, 2.2rem)',
          fontWeight: 700, color: '#1A1A2E', margin: 0,
        }}>
          {isFr ? 'Programmes' : 'Programs'}
        </h1>
        <p style={{ color: '#64748B', fontSize: '0.95rem', marginTop: '0.35rem', fontFamily: 'Nunito, sans-serif' }}>
          {(() => {
            const visibleGroupCount = new Set(programs.map(p => p.audience_type)).size;
            return isFr
              ? `${programs.length} programmes complets pour ${visibleGroupCount} types d'institutions`
              : `${programs.length} complete programs for ${visibleGroupCount} institution types`;
          })()}
        </p>
      </div>

      {/* Audience Groups */}
      {grouped.map(group => {
        if (group.programs.length === 0) return null;
        const GroupIcon = group.icon;
        return (
          <div key={group.key} style={{ marginBottom: '2.5rem' }}>
            {/* Group Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: group.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <GroupIcon size={18} color={group.color} />
              </div>
              <h2 style={{
                fontFamily: 'Outfit, sans-serif', fontSize: '1.15rem', fontWeight: 600,
                color: '#1A1A2E', margin: 0,
              }}>
                {isFr ? group.labelFr : group.labelEn}
              </h2>
              <span style={{
                fontSize: '0.8rem', color: '#94A3B8', fontWeight: 500,
                background: '#F1F5F9', borderRadius: 12, padding: '2px 10px',
              }}>
                {group.programs.length}
              </span>
            </div>

            {/* Program Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '1rem',
            }}>
              {group.programs.map(prog => (
                <div
                  key={prog.program_id}
                  data-testid={`program-card-${prog.program_id}`}
                  onClick={() => navigate(`/programs/${prog.program_id}`)}
                  style={{
                    background: 'white', borderRadius: 12, padding: '1.25rem',
                    border: '1px solid #E2E8F0',
                    borderLeft: `4px solid ${prog.color || group.color}`,
                    cursor: 'pointer',
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h3 style={{
                      fontFamily: 'Outfit, sans-serif', fontSize: '1rem', fontWeight: 600,
                      color: '#1A1A2E', margin: 0, flex: 1, lineHeight: 1.3,
                    }}>
                      {isFr ? (prog.title_fr || prog.title) : prog.title}
                    </h3>
                    <ChevronRight size={18} color="#94A3B8" style={{ flexShrink: 0, marginTop: 2 }} />
                  </div>

                  {/* CEFR Badge */}
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem', flexWrap: 'wrap' }}>
                    <span style={{
                      fontSize: '0.75rem', fontWeight: 600, color: prog.color || group.color,
                      background: (prog.color || group.color) + '12',
                      borderRadius: 6, padding: '2px 8px',
                    }}>
                      {prog.cefr_start} → {prog.cefr_end}
                    </span>
                    <span style={{
                      fontSize: '0.75rem', color: '#64748B', display: 'flex', alignItems: 'center', gap: 3,
                    }}>
                      <Clock size={12} /> {prog.total_sessions} {isFr ? 'seances' : 'sessions'}
                    </span>
                    {prog.week_count > 0 && (
                      <span style={{ fontSize: '0.75rem', color: '#64748B', display: 'flex', alignItems: 'center', gap: 3 }}>
                        <BarChart3 size={12} /> {prog.week_count} {isFr ? 'sem.' : 'weeks'}
                      </span>
                    )}
                  </div>

                  {/* Description */}
                  <p style={{
                    fontSize: '0.82rem', color: '#64748B', marginTop: '0.6rem',
                    lineHeight: 1.5, fontFamily: 'Nunito, sans-serif',
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}>
                    {prog.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
